import {
  Alert,
  Button,
  Col,
  Flex,
  Modal,
  Row,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  LoadingOutlined,
  MinusCircleOutlined,
  PlayCircleOutlined,
  PlaySquareOutlined,
  RedoOutlined
} from '@ant-design/icons';
import { EditButton } from '@refinedev/antd';
import { IContent, IInteractiveClass } from 'interfaces/interactive_classes';
import { PageHeader } from 'components/professor_area/PageHeader';
import { ShowFieldData } from 'components/ShowFieldData';
import { ShowLayout } from 'components/layout/layout/ShowLayout';
import { formatDate } from 'services/date';
import {
  getStartUrlUnavailableReason,
  isStartUrlAvailable
} from 'services/professor_area/interactive-classes';
import { getAPI } from '../../../requests/api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCan, useDelete, useNotification, useShow } from '@refinedev/core';

const ALL_RECORDINGS = 'all';

const isContentPublished = (content: IContent) => content.published !== false;

const getContentVersion = (content: IContent) =>
  isContentPublished(content)
    ? content.default_version
    : content.latest_version;

const hasSameOrder = (currentOrder: string[], nextOrder: string[]) =>
  currentOrder.length === nextOrder.length &&
  currentOrder.every((contentId, index) => contentId === nextOrder[index]);

const openVideoPreview = (name: string, deliveryUrl: string) => {
  Modal.info({
    title: name,
    content: (
      <div style={{ height: '500px', width: '100%' }}>
        <iframe
          src={deliveryUrl}
          style={{
            width: '100%',
            height: '100%',
            border: 'none'
          }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    ),
    width: 800,
    okText: 'Fechar'
  });
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'created': {
      return 'grey';
    }

    case 'started': {
      return 'green';
    }

    case 'finished': {
      return 'grey';
    }

    case 'cancelled': {
      return 'red';
    }

    case 'suspended': {
      return 'yellow';
    }

    default: {
      return 'default';
    }
  }
};

const translateStatus = (status: string) => {
  switch (status) {
    case 'created': {
      return 'Criado';
    }

    case 'started': {
      return 'Iniciado';
    }

    case 'finished': {
      return 'Finalizado';
    }

    case 'cancelled': {
      return 'Cancelado';
    }

    case 'suspended': {
      return 'Suspensa';
    }

    default: {
      return status;
    }
  }
};

export const InteractiveClassShow = () => {
  const { data: canShow } = useCan({
    resource: 'interactive_classes',
    action: 'show'
  });
  const { data: canDelete } = useCan({
    resource: 'interactive_classes',
    action: 'delete'
  });
  const { data: canEdit } = useCan({
    resource: 'interactive_classes',
    action: 'edit'
  });
  const hasShowPermission = !!canShow?.can;
  const hasDeletePermission = !!canDelete?.can;
  const hasEditPermission = !!canEdit?.can;

  const { queryResult } = useShow<IInteractiveClass>({
    queryOptions: {
      enabled: hasShowPermission
    }
  });
  const { mutateAsync: deleteInteractiveClass, isLoading: isDeleting } =
    useDelete<IInteractiveClass>();
  const notification = useNotification();
  const [loading, setLoading] = useState(false);
  const [updatingVideoId, setUpdatingVideoId] = useState<string | null>(null);
  const [reprocessingId, setReprocessingId] = useState<string | null>(null);
  const [videoOrder, setVideoOrder] = useState<string[]>([]);
  const [modal, contextHolder] = Modal.useModal();

  const result = queryResult.data?.data;
  const videoContentsFromResult = result?.meeting.contents;
  const videoContents = useMemo(() => {
    const contents = videoContentsFromResult ?? [];
    const orderByContentId = new Map(
      videoOrder.map((contentId, index) => [contentId, index])
    );

    return [...contents].sort((firstContent, secondContent) => {
      const firstOrder = orderByContentId.get(firstContent.id);
      const secondOrder = orderByContentId.get(secondContent.id);

      if (firstOrder === undefined && secondOrder === undefined) {
        return 0;
      }

      if (firstOrder === undefined) {
        return 1;
      }

      if (secondOrder === undefined) {
        return -1;
      }

      return firstOrder - secondOrder;
    });
  }, [videoContentsFromResult, videoOrder]);

  useEffect(() => {
    if (!videoContentsFromResult) {
      return;
    }

    const nextContentIds = videoContentsFromResult.map(content => content.id);

    setVideoOrder(currentOrder => {
      const currentContentIds = new Set(nextContentIds);
      const orderedCurrentIds = currentOrder.filter(contentId =>
        currentContentIds.has(contentId)
      );
      const newContentIds = nextContentIds.filter(
        contentId => !currentOrder.includes(contentId)
      );
      const nextOrder = [...orderedCurrentIds, ...newContentIds];

      return hasSameOrder(currentOrder, nextOrder) ? currentOrder : nextOrder;
    });
  }, [videoContentsFromResult]);

  const handleUpdateVideoPublication = useCallback(
    async (
      contentId: string,
      publishedVersionNumber: 'unpublish' | 'latest'
    ) => {
      if (!hasEditPermission || !result?.id) {
        return;
      }

      setUpdatingVideoId(contentId);

      try {
        await getAPI().put(
          `/admin/interactive_classes/${result.id}/update_video_publication`,
          {
            content_id: contentId,
            published_version_number: publishedVersionNumber
          }
        );

        notification.open?.({
          type: 'success',
          message: 'Sucesso',
          description:
            publishedVersionNumber === 'unpublish'
              ? 'Vídeo ocultado para os alunos com sucesso.'
              : 'Vídeo publicado com sucesso.'
        });

        await queryResult.refetch();
      } catch {
        notification.open?.({
          type: 'error',
          message: 'Erro',
          description: 'Não foi possível atualizar a publicação do vídeo.'
        });
      } finally {
        setUpdatingVideoId(null);
      }
    },
    [hasEditPermission, notification, queryResult, result?.id]
  );

  // Contents whose Zoom download failed halfway through stay corrupted forever: the ingestion
  // schedules only look at recordings that have no content yet, so they never come back to them.
  const requestReprocess = useCallback(
    async (contentId?: string) => {
      if (!hasEditPermission || !result?.id) {
        return;
      }

      setReprocessingId(contentId ?? ALL_RECORDINGS);

      try {
        await getAPI().post(
          `/admin/interactive_classes/${result.id}/reprocess_recordings`,
          contentId ? { content_ids: [contentId] } : {}
        );

        notification.open?.({
          type: 'success',
          message: 'Reprocessamento iniciado',
          description:
            'As gravações estão sendo baixadas do Zoom novamente. Isso leva alguns minutos — atualize a página para acompanhar.'
        });

        await queryResult.refetch();
      } catch {
        notification.open?.({
          type: 'error',
          message: 'Erro',
          description:
            'Não foi possível iniciar o reprocessamento das gravações.'
        });
      } finally {
        setReprocessingId(null);
      }
    },
    [hasEditPermission, notification, queryResult, result?.id]
  );

  const handleReprocess = useCallback(
    (contentId?: string) => {
      modal.confirm({
        title: contentId
          ? 'Baixar gravação novamente'
          : 'Baixar todas as gravações novamente',
        content: contentId
          ? 'O vídeo atual será descartado e baixado do Zoom outra vez. Ele fica indisponível para os alunos até o novo processamento terminar. Deseja continuar?'
          : 'Todos os vídeos desta aula serão descartados e baixados do Zoom outra vez. Eles ficam indisponíveis para os alunos até o novo processamento terminar. Deseja continuar?',
        okText: 'Sim, baixar novamente',
        cancelText: 'Não',
        onOk: () => requestReprocess(contentId)
      });
    },
    [modal, requestReprocess]
  );

  const handleCancel = useCallback(() => {
    if (!hasDeletePermission) {
      return;
    }

    modal.confirm({
      title: 'Cancelar aula',
      content: 'Tem certeza que deseja cancelar a aula?',
      okText: 'Sim',
      cancelText: 'Não',
      okType: 'danger',
      onOk: () =>
        deleteInteractiveClass({
          id: result?.id || '',
          resource: 'interactive_classes'
        })
    });
  }, [modal, result?.id, deleteInteractiveClass, hasDeletePermission]);

  if (!result) {
    return null;
  }

  const canStartClass = isStartUrlAvailable(
    result.start_time,
    result.status
  );
  const startUrlUnavailableReason = getStartUrlUnavailableReason(
    result.start_time,
    result.status
  );

  const downloadReportFile = async () => {
    if (!hasShowPermission) {
      return;
    }

    setLoading(true);

    try {
      const response = await getAPI().get(
        `/admin/interactive_classes/${result.id}/participants_report`,
        {
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'relatorio_participantes.csv');
      document.body.append(link);
      link.click();

      window.URL.revokeObjectURL(url);
      link.remove();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.status === 404) {
        notification.open?.({
          type: 'error',
          message: 'Download Error',
          description: 'Não há relatório de participantes disponível.'
        });
      } else {
        notification.open?.({
          type: 'error',
          message: 'Download Error',
          description: 'Erro ao baixar o relatório de participantes.'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <ShowLayout isLoading={queryResult.isLoading}>
      <PageHeader
        title={result?.title}
        icon={PlaySquareOutlined}
        meta={
          <Flex>
            <Tag color={getStatusColor(result?.status)}>
              {translateStatus(result?.status)}
            </Tag>
            <Tag>
              {formatDate(
                result?.start_time,
                'DD/MM/YYYY [às] HH:mm[h] ([GMT]Z)'
              )}
            </Tag>
          </Flex>
        }
        actions={
          <Flex gap={16}>
            <EditButton
              type="primary"
              accessControl={{ hideIfUnauthorized: true }}
            />
            {hasDeletePermission && result?.status !== 'cancelled' && (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleCancel}
                loading={isDeleting}
              >
                Cancelar
              </Button>
            )}
            {contextHolder}
          </Flex>
        }
      />

      {!queryResult.isLoading && (
        <Tabs defaultActiveKey="general" className="custom-tabs">
          {hasShowPermission && (
            <Tabs.TabPane tab="Informações Gerais" key="general">
              <Space direction="vertical" size="large" className="w-full">
                <Row gutter={[16, 24]}>
                  <Col xs={24} md={24}>
                    <ShowFieldData label="ID da aula" value={result?.id} />
                  </Col>
                </Row>

                {result?.start_url && (
                  <Row gutter={[16, 24]}>
                    <Col xs={24} md={24}>
                      <ShowFieldData
                        label="Link do professor"
                        value={
                          <>
                            {canStartClass ? (
                              <Button
                                type="primary"
                                className="mt-2"
                                icon={<PlayCircleOutlined />}
                                onClick={() =>
                                  window.open(result?.start_url, '_blank')
                                }
                              >
                                Iniciar aula
                              </Button>
                            ) : (
                              <Space direction="vertical" className="w-full">
                                <Button
                                  disabled
                                  type="primary"
                                  className="mt-2"
                                  icon={<PlayCircleOutlined />}
                                >
                                  Iniciar aula
                                </Button>
                                <Alert
                                  message={startUrlUnavailableReason}
                                  type="warning"
                                  showIcon
                                  className="w-fit"
                                />
                              </Space>
                            )}
                          </>
                        }
                      />
                    </Col>
                  </Row>
                )}
                <Row gutter={[16, 24]}>
                  <Col xs={24} md={24}>
                    <ShowFieldData
                      label="Título da aula"
                      value={result?.title}
                    />
                  </Col>
                </Row>
                {result?.description && (
                  <Row gutter={[16, 24]}>
                    <Col xs={24} md={24}>
                      <ShowFieldData
                        label="Descrição"
                        value={result?.description}
                      />
                    </Col>
                  </Row>
                )}
                <Row gutter={[16, 24]}>
                  <Col xs={24} md={24}>
                    <ShowFieldData
                      label="Data"
                      value={formatDate(
                        result?.start_time,
                        'DD/MM/YYYY [às] HH:mm[h] ([GMT]Z)'
                      )}
                    />
                  </Col>
                </Row>
                <Row gutter={[16, 24]}>
                  <Col xs={24} md={24}>
                    <ShowFieldData
                      label="Professores"
                      value={result?.professors
                        .map(
                          professor => `${professor.name} (${professor.email})`
                        )
                        .join('\n')}
                    />
                  </Col>
                </Row>
                <Row gutter={[16, 24]}>
                  <Col xs={24} md={24}>
                    <ShowFieldData
                      label="Turmas"
                      value={result?.classrooms
                        .map(
                          classroom =>
                            `${classroom.discipline.name} - ${classroom.code}`
                        )
                        .join(', ')}
                    />
                  </Col>
                </Row>

                <Row gutter={[16, 24]}>
                  <Col xs={24} md={24}>
                    <ShowFieldData
                      label="Status"
                      value={
                        <Tag color={getStatusColor(result?.status)}>
                          {translateStatus(result?.status)}
                        </Tag>
                      }
                    />
                  </Col>
                </Row>

                <Row gutter={[16, 24]}>
                  <Col xs={24} md={24}>
                    <ShowFieldData
                      label="Controle de Frequência"
                      value={
                        <Button
                          icon={
                            loading ? <LoadingOutlined /> : <DownloadOutlined />
                          }
                          onClick={downloadReportFile}
                          disabled={result.status !== 'finished' || loading}
                        >
                          Baixar dados da aula realizada
                        </Button>
                      }
                    />
                  </Col>
                </Row>
              </Space>
            </Tabs.TabPane>
          )}
          {hasShowPermission && (
            <Tabs.TabPane tab={`Videos (${videoContents.length})`} key="videos">
              {hasEditPermission && (
                <Flex
                  justify="space-between"
                  align="center"
                  gap={16}
                  className="mb-4"
                >
                  {/* Sem `description`: o que o reprocessamento faz e o que custa já está no
                      modal de confirmação, que é onde a pessoa realmente decide. */}
                  <Alert
                    type="info"
                    showIcon
                    message="Vídeo corrompido ou incompleto? Baixe a gravação do Zoom novamente."
                  />
                  <Button
                    icon={<RedoOutlined />}
                    onClick={() => handleReprocess()}
                    loading={reprocessingId === ALL_RECORDINGS}
                    disabled={reprocessingId !== null}
                  >
                    Baixar tudo novamente
                  </Button>
                </Flex>
              )}
              <Table
                dataSource={videoContents}
                rowKey="id"
                pagination={false}
                columns={[
                  {
                    title: 'Título',
                    dataIndex: 'name',
                    key: 'title'
                  },
                  {
                    title: 'Data de Criação',
                    key: 'created_at',
                    render: (_, record) => {
                      const createdAt = getContentVersion(record)?.created_at;

                      return createdAt ? formatDate(createdAt) : '-';
                    }
                  },
                  {
                    title: 'Status',
                    key: 'status',
                    render: (_, record) => (
                      <Tag
                        color={isContentPublished(record) ? 'green' : 'default'}
                      >
                        {isContentPublished(record)
                          ? 'Publicado'
                          : 'Não publicado'}
                      </Tag>
                    )
                  },
                  {
                    title: 'Ações',
                    key: 'actions',
                    width: 180,
                    align: 'center',
                    render: (_, record) => {
                      const version = getContentVersion(record);
                      const deliveryUrl =
                        version?.delivery_option?.delivery_url;
                      const isUpdating = updatingVideoId === record.id;
                      const isPublished = isContentPublished(record);
                      const isProcessing = !deliveryUrl;
                      const isReprocessing = reprocessingId === record.id;

                      return (
                        <Flex align="center" justify="center" gap={4}>
                          {isProcessing ? (
                            <Tooltip title="Em processamento">
                              <Button
                                type="text"
                                icon={
                                  <ClockCircleOutlined
                                    style={{ fontSize: 18 }}
                                  />
                                }
                                disabled
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Visualizar vídeo">
                              <Button
                                type="text"
                                icon={
                                  <PlayCircleOutlined
                                    style={{ fontSize: 18 }}
                                  />
                                }
                                onClick={() =>
                                  openVideoPreview(record.name, deliveryUrl!)
                                }
                              />
                            </Tooltip>
                          )}

                          {hasEditPermission &&
                            (isPublished ? (
                              <Tooltip title="Remover publicação do vídeo para o aluno">
                                <Button
                                  type="text"
                                  icon={
                                    isUpdating ? (
                                      <LoadingOutlined
                                        style={{ fontSize: 18 }}
                                      />
                                    ) : (
                                      <MinusCircleOutlined
                                        style={{
                                          fontSize: 18,
                                          color: '#ff4d4f'
                                        }}
                                      />
                                    )
                                  }
                                  disabled={isUpdating}
                                  onClick={() =>
                                    handleUpdateVideoPublication(
                                      record.id,
                                      'unpublish'
                                    )
                                  }
                                />
                              </Tooltip>
                            ) : (
                              <Tooltip title="Publicar vídeo para o aluno">
                                <Button
                                  type="text"
                                  icon={
                                    isUpdating ? (
                                      <LoadingOutlined
                                        style={{ fontSize: 18 }}
                                      />
                                    ) : (
                                      <CheckCircleOutlined
                                        style={{
                                          fontSize: 18,
                                          color: '#52c41a'
                                        }}
                                      />
                                    )
                                  }
                                  disabled={isUpdating}
                                  onClick={() =>
                                    handleUpdateVideoPublication(
                                      record.id,
                                      'latest'
                                    )
                                  }
                                />
                              </Tooltip>
                            ))}

                          {hasEditPermission && (
                            <Tooltip title="Baixar esta gravação do Zoom novamente">
                              <Button
                                type="text"
                                icon={
                                  isReprocessing ? (
                                    <LoadingOutlined style={{ fontSize: 18 }} />
                                  ) : (
                                    <RedoOutlined style={{ fontSize: 18 }} />
                                  )
                                }
                                disabled={reprocessingId !== null}
                                onClick={() => handleReprocess(record.id)}
                              />
                            </Tooltip>
                          )}
                        </Flex>
                      );
                    }
                  }
                ]}
              />
            </Tabs.TabPane>
          )}
        </Tabs>
      )}
    </ShowLayout>
  );
};
